import {
  CloseCircleOutline,
  SearchDropdownInput,
  DropdownOption,
  Label,
  individualFullName,
  AddGroupMembershipRequest,
  UpdateGroupMembershipRequest,
  useToast,
} from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

import useGroups from 'app/hooks/useGroups';
import useInfiniteFetchEmployees from 'app/hooks/useInfiniteFetchEmployees';
import FailedMembersSummary from 'app/components/GroupMemberships/FailedMembersSummary';
import { FailedMember, resolveMembershipOutcomes } from 'app/utils/groupMembership';

import { AddPotManagerModalProps } from './AddPotManagerModal.types';

const PAGE_SIZE = 20;

function AddPotManagerModal({
  isOpen,
  onClose,
  originGroupWithPot,
  companyId,
}: AddPotManagerModalProps) {
  const toast = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedNamesMap, setSelectedNamesMap] = useState<Map<string, string>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [failedMembers, setFailedMembers] = useState<FailedMember[]>([]);

  const { mutateAsync: addGroupMemberships } = useGroups.useAddGroupMemberships(
    companyId,
    originGroupWithPot?.id
  );

  const { mutateAsync: updateGroupMemberships } = useGroups.useUpdateGroupMemberships(
    companyId,
    originGroupWithPot?.id
  );

  const { data: membershipPages } = useGroups.useFetchGroupMemberships(
    companyId,
    originGroupWithPot?.id,
    { status: 'ACTIVE' },
    !!originGroupWithPot && isOpen
  );

  const { members, potManagers } = useMemo(() => {
    const allMemberships = membershipPages?.pages.flatMap((p) => p.list) ?? [];

    return {
      members: new Map(
        allMemberships
          .filter((p) => p.membershipRole === 'MEMBER')
          .map((m) => [m.individualId, m.isPrimaryGroup])
      ),
      potManagers: new Set(
        allMemberships.filter((p) => p.membershipRole === 'POT_MANAGER').map((m) => m.individualId)
      ),
    };
  }, [membershipPages]);

  const {
    data: employeePages,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteFetchEmployees({
    companyId: companyId ? [companyId] : [],
    query: searchQuery.trim() || undefined,
    role: ['EMPLOYEE'],
    status: ['ACTIVE', 'CREATED', 'INVITED'],
    size: PAGE_SIZE,
    sort: 'ASC',
    sortBy: 'firstName',
    enabled: !!companyId && isOpen,
  });

  const options: DropdownOption[] = useMemo(() => {
    const allEmployees = employeePages?.pages.flatMap((p) => p.list) ?? [];
    return allEmployees.map((e) => ({
      value: e.id,
      text: individualFullName(e),
      email: e.email,
      isMember: members.has(e.id),
      isPotManager: potManagers.has(e.id),
      isDisabled: potManagers.has(e.id),
    }));
  }, [employeePages, potManagers, members]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  };

  const handleConfirm = async () => {
    const toAdd: AddGroupMembershipRequest = [];
    const toUpdate: UpdateGroupMembershipRequest = [];

    selected.forEach((id) => {
      if (members.has(id)) {
        toUpdate.push({
          individualId: id,
          membershipRole: 'POT_MANAGER',
          isPrimaryGroup: members.get(id) || false,
        });
      } else {
        toAdd.push({
          individualId: id,
          membershipRole: 'POT_MANAGER',
          isPrimaryGroup: false,
        });
      }
    });

    const total = toAdd.length + toUpdate.length;
    setIsConfirming(true);
    setFailedMembers([]);

    const [addOutcome, updateOutcome] = await Promise.allSettled([
      toAdd.length > 0 ? addGroupMemberships(toAdd) : Promise.resolve(undefined),
      toUpdate.length > 0 ? updateGroupMemberships(toUpdate) : Promise.resolve(undefined),
    ]);

    const nameById = (id: string) => selectedNamesMap.get(id);

    // A 200 gives per-row outcomes; a wholesale rejection (network / 5xx) means none of that call's rows applied.
    const { failed, succeededCount } = resolveMembershipOutcomes(
      [
        {
          result: addOutcome.status === 'fulfilled' ? addOutcome.value : undefined,
          rejectedIds: addOutcome.status === 'rejected' ? toAdd.map((m) => m.individualId) : [],
        },
        {
          result: updateOutcome.status === 'fulfilled' ? updateOutcome.value : undefined,
          rejectedIds:
            updateOutcome.status === 'rejected' ? toUpdate.map((m) => m.individualId) : [],
        },
      ],
      nameById
    );

    setIsConfirming(false);

    if (failed.length === 0) {
      toast({
        position: 'top-right',
        status: 'success',
        title: `${total} pot manager(s) added to ${originGroupWithPot?.pot?.name}`,
        description: `The selected employee(s) now manage this pot.`,
      });
      onClose();
      return;
    }

    // Keep the failed members selected so the admin can retry them, and show why each failed.
    const failedIds = new Set(failed.map((f) => f.id));
    setSelected((prev) => prev.filter((id) => failedIds.has(id)));
    setFailedMembers(failed);

    if (succeededCount === 0) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'No pot managers added',
        description: `None of the selected employee(s) could be added. See the reasons below.`,
      });
    } else {
      toast({
        position: 'top-right',
        status: 'warning',
        title: `${succeededCount} of ${total} pot manager(s) added`,
        description: `Some managers could not be added. See the reasons below.`,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelected([]);
      setSelectedNamesMap(new Map());
      setSearchQuery('');
      setIsConfirming(false);
      setFailedMembers([]);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent w={{ md: '600px' }} p="24px 24px 24px 24px" position="relative">
        <>
          <ModalHeader p="0">
            <Flex direction="column" gap="20px" pb="5px">
              <Flex justify="space-between" align="center">
                <Flex>
                  <Box>
                    <Text as="h6" textStyle="h6SemiBold" color="brand.black">
                      Add pot manager
                    </Text>
                  </Box>
                </Flex>
                <IconButton
                  aria-label="Close modal"
                  icon={<CloseCircleOutline boxSize="30px" />}
                  variant="link"
                  onClick={onClose}
                  color="brand.purple"
                />
              </Flex>
            </Flex>
          </ModalHeader>

          <ModalBody p="0" gap="20px" display="flex" flexDirection="column">
            <Text>
              Search for an employee to add as pot manager. You can add multiple managers at once.
            </Text>
            <Flex direction="column" gap="15px">
              {selected.length > 0 && (
                <Flex gap="6px" flexWrap="wrap">
                  {selected.map((val) => (
                    <Label
                      size="sm"
                      key={val}
                      label={selectedNamesMap.get(val) ?? val}
                      onClose={() => setSelected(selected.filter((v) => v !== val))}
                    />
                  ))}
                </Flex>
              )}
              <SearchDropdownInput
                placeholder="Search employees"
                itemName="employee(s)"
                options={options}
                selected={selected}
                onChange={(newSelected) => {
                  setSelected(newSelected);
                  setSelectedNamesMap((prev) => {
                    const next = new Map(prev);
                    newSelected.forEach((id) => {
                      if (!next.has(id)) {
                        const opt = options.find((o) => String(o.value) === id);
                        if (opt) next.set(id, opt.text);
                      }
                    });
                    prev.forEach((_, id) => {
                      if (!newSelected.includes(id)) next.delete(id);
                    });
                    return next;
                  });
                }}
                onSearch={setSearchQuery}
                searchDebounceDelay={300}
                isLoading={isFetching && !isFetchingNextPage}
                isLoadingMore={isFetchingNextPage}
                onLoadMore={handleLoadMore}
                renderText={(option) => {
                  return (
                    <Flex direction="column" flex="1">
                      <Flex direction="row" flex="1">
                        <Text as="p" textStyle="smallMedium">
                          {option.text}
                        </Text>
                        <>
                          <Spacer />
                          <Flex direction="row" gap="5px" align="center">
                            <Text as="span" textStyle="smallRegular">
                              {option.email}
                            </Text>
                          </Flex>
                        </>
                      </Flex>

                      <Text as="span" textStyle="smallRegular">
                        {option.isMember && 'Already a member of this pot'}
                        {option.isPotManager && 'Already a pot manager'}
                      </Text>
                    </Flex>
                  );
                }}
              />
            </Flex>
            <FailedMembersSummary failedMembers={failedMembers} />
          </ModalBody>
          <ModalFooter gap="12px">
            <Button variant="outline" onClick={onClose} isDisabled={isConfirming}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              isDisabled={selected.length === 0}
              isLoading={isConfirming}
            >
              Add manager(s)
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
}

export default AddPotManagerModal;
